import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';
import QRCode from 'qrcode';

// Explicit 2FA state — never treat unknown as disabled.
// 'loading'  = check in progress, block access
// 'enabled'  = 2FA is on, require TOTP verification
// 'disabled' = 2FA is explicitly off (confirmed from DB)
// 'error'    = lookup failed or profile missing, block access
export type TwoFAState = 'loading' | 'enabled' | 'disabled' | 'error';

interface TwoFactorContextType {
  twoFAState: TwoFAState;
  // Derived convenience bool — true only when state === 'enabled'
  is2FAEnabled: boolean;
  // True once the initial status check has settled (any terminal state)
  statusChecked: boolean;
  qrCodeUrl: string | null;
  manualEntryKey: string | null;
  isLoading: boolean;
  error: string | null;
  setup2FA: () => Promise<void>;
  verify2FA: (code: string, enable?: boolean) => Promise<boolean>;
  disable2FA: () => Promise<void>;
  check2FAStatus: () => Promise<void>;
}

const TwoFactorContext = createContext<TwoFactorContextType | undefined>(undefined);

export const useTwoFactor = () => {
  const context = useContext(TwoFactorContext);
  if (!context) {
    throw new Error('useTwoFactor must be used within a TwoFactorProvider');
  }
  return context;
};

interface TwoFactorProviderProps {
  children: ReactNode;
}

export const TwoFactorProvider: React.FC<TwoFactorProviderProps> = ({ children }) => {
  const { user, userType } = useAuth();
  const [twoFAState, setTwoFAState] = useState<TwoFAState>('loading');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [manualEntryKey, setManualEntryKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derived values
  const is2FAEnabled = twoFAState === 'enabled';
  const statusChecked = twoFAState !== 'loading';

  const check2FAStatus = async () => {
    // No user = no session = block access until auth resolves
    if (!user) {
      setTwoFAState('loading');
      return;
    }

    // Unknown userType = unresolved role = block access
    if (!userType) {
      console.warn('[2FA] userType not yet resolved — blocking access until role is known');
      setTwoFAState('loading');
      return;
    }

    // Transition to loading for the duration of this check
    setTwoFAState('loading');

    try {
      if (userType === 'admin') {
        const { data, error: dbError } = await supabase
          .from('user_roles')
          .select('is_2fa_enabled')
          .eq('user_id', user.id)
          .maybeSingle();

        if (dbError) {
          console.error('[2FA] Failed to fetch admin 2FA status from user_roles:', dbError);
          setTwoFAState('error');
          return;
        }
        if (data === null) {
          console.warn('[2FA] SECURITY WARNING: No user_roles row found for admin user:', user.id, '— blocking access');
          setTwoFAState('error');
          return;
        }
        setTwoFAState(data.is_2fa_enabled ? 'enabled' : 'disabled');
        return;
      }

      if (userType === 'consultant') {
        const { data, error: dbError } = await supabase
          .from('consultant_profiles')
          .select('is_2fa_enabled')
          .eq('id', user.id)
          .maybeSingle();

        if (dbError) {
          console.error('[2FA] Failed to fetch consultant 2FA status from consultant_profiles:', dbError);
          setTwoFAState('error');
          return;
        }
        if (data === null) {
          console.warn('[2FA] SECURITY WARNING: No consultant_profiles row found for consultant user:', user.id, '— blocking access');
          setTwoFAState('error');
          return;
        }
        setTwoFAState(data.is_2fa_enabled ? 'enabled' : 'disabled');
        return;
      }

      if (userType === 'workday_services_client') {
        const { data, error: dbError } = await supabase
          .from('workday_client_profiles')
          .select('is_2fa_enabled')
          .eq('id', user.id)
          .maybeSingle();

        if (dbError) {
          console.error('[2FA] Failed to fetch client 2FA status from workday_client_profiles:', dbError);
          setTwoFAState('error');
          return;
        }
        if (data === null) {
          console.warn('[2FA] SECURITY WARNING: No workday_client_profiles row found for client user:', user.id, '— blocking access');
          setTwoFAState('error');
          return;
        }
        setTwoFAState(data.is_2fa_enabled ? 'enabled' : 'disabled');
        return;
      }

      // Unrecognised role — block access, do not probe tables
      console.warn('[2FA] SECURITY WARNING: Unrecognised userType:', userType, 'for user:', user.id, '— blocking access');
      setTwoFAState('error');
    } catch (err) {
      // Network failure, RLS rejection, or any unexpected error — FAIL CLOSED
      console.error('[2FA] Unexpected error during 2FA status check — blocking access:', err);
      setTwoFAState('error');
    }
  };

  useEffect(() => {
    // Reset to loading whenever user or role changes so access is blocked
    // until the new check resolves.
    setTwoFAState('loading');
    check2FAStatus();
  }, [user?.id, userType]);

  const setup2FA = async () => {
    if (!user) {
      setError('You must be logged in to set up 2FA');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-setup`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to set up 2FA');
      }

      const data = await response.json();

      const qrDataUrl = await QRCode.toDataURL(data.otpauth_url);
      setQrCodeUrl(qrDataUrl);
      setManualEntryKey(data.manual_entry_key);
    } catch (err) {
      console.error('Error setting up 2FA:', err);
      setError(err instanceof Error ? err.message : 'Failed to set up 2FA');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const verify2FA = async (code: string, enable: boolean = false): Promise<boolean> => {
    if (!user) {
      setError('You must be logged in to verify 2FA');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-verify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ token: code, enable }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Invalid 2FA code');
      }

      if (enable) {
        setTwoFAState('enabled');
        setQrCodeUrl(null);
        setManualEntryKey(null);
      }

      return true;
    } catch (err) {
      console.error('Error verifying 2FA:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify 2FA code');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!user) {
      setError('You must be logged in to disable 2FA');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-disable`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to disable 2FA');
      }

      setTwoFAState('disabled');
      setQrCodeUrl(null);
      setManualEntryKey(null);
    } catch (err) {
      console.error('Error disabling 2FA:', err);
      setError(err instanceof Error ? err.message : 'Failed to disable 2FA');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const value: TwoFactorContextType = {
    twoFAState,
    is2FAEnabled,
    statusChecked,
    qrCodeUrl,
    manualEntryKey,
    isLoading,
    error,
    setup2FA,
    verify2FA,
    disable2FA,
    check2FAStatus,
  };

  return <TwoFactorContext.Provider value={value}>{children}</TwoFactorContext.Provider>;
};
